/**
 * The client-side shape of a submission.
 *
 * The server deliberately does NOT import these types. It restates the contract
 * in `server/src/validation/payload.ts` as an independent check — a server that
 * shares the client's types is a server that trusts the client. If you change a
 * union here, change it there too; the SQLite CHECK constraints are the backstop.
 */

/**
 * Building age band. `nustiu` exists for the ICP — many passive owners simply do
 * not know. The value must be accepted by the server allowlist AND the SQLite
 * CHECK. En dash inside the year ranges is the one dash the copy rules allow.
 */
export const AN_CONSTRUCTIE_VALUES = ['pre1945', 'y1945_1963', 'y1964_1977', 'y1978_1989', 'post1990', 'post2010', 'nustiu'] as const;
export type AnConstructie = (typeof AN_CONSTRUCTIE_VALUES)[number];

export const TIP_IMOBIL_VALUES = ['apartament', 'casa', 'altul'] as const;
export type TipImobil = (typeof TIP_IMOBIL_VALUES)[number];

export const NUMAR_CAMERE_VALUES = ['1', '2', '3', '4', '5plus'] as const;
export type NumarCamere = (typeof NUMAR_CAMERE_VALUES)[number];

/**
 * Seismic classification — the dimension land does not carry. `rsi` is the red
 * dot ("bulină roșie"), the class that most changes a renovation calculus.
 * `urgente` groups the emergency categories U1–U3; `consolidat` is a building
 * that has already been strengthened. The server allowlist AND the SQLite CHECK
 * must both know any value here — and the CHECK also tolerates the retired
 * `alta` for rows captured before this list changed.
 */
export const INCADRARE_SEISMICA_VALUES = ['rsi', 'rsii', 'rsiii', 'urgente', 'fara', 'consolidat', 'nustiu'] as const;
export type IncadrareSeismica = (typeof INCADRARE_SEISMICA_VALUES)[number];

/**
 * The rest of step 1 splits by building type. An apartment carries a layout, a
 * balcony and a heating source; a house/other carries a structural material and
 * its own heating. `StepApartment` shows one group or the other from `tipImobil`.
 * All optional, all shape-checked by the server allowlist and the SQLite CHECK.
 */
export const COMPARTIMENTARE_VALUES = ['decomandat', 'semidecomandat', 'nedecomandat', 'circular', 'open_space'] as const;
export type Compartimentare = (typeof COMPARTIMENTARE_VALUES)[number];

export const BALCON_VALUES = ['nu', 'deschis', 'inchis'] as const;
export type Balcon = (typeof BALCON_VALUES)[number];

export const SURSA_INCALZIRE_VALUES = ['centrala_proprie', 'termoenergetica', 'centrala_bloc', 'calorifere_electrice'] as const;
export type SursaIncalzire = (typeof SURSA_INCALZIRE_VALUES)[number];

export const STRUCTURA_VALUES = ['cadre_beton_armat', 'zidarie_portanta', 'lemn', 'metalica', 'chirpici_paianta'] as const;
export type Structura = (typeof STRUCTURA_VALUES)[number];

export const INCALZIRE_VALUES = ['centrala_gaz', 'lemne', 'pompa_caldura', 'fara'] as const;
export type Incalzire = (typeof INCALZIRE_VALUES)[number];

export const STARE_VALUES = ['renovat_recent', 'renovat_invechit', 'locuibil_nerenovat', 'necesita_renovare_completa'] as const;
export type Stare = (typeof STARE_VALUES)[number];

/**
 * Legal status of the apartment. Reused verbatim from the land tool — same
 * options, same disarming help text. Optional like the rest of step 2, and the
 * server allowlist AND the SQLite CHECK must both know any value added here.
 */
export const SITUATIE_JURIDICA_VALUES = [
  'unic',
  'coproprietari',
  'succesiune',
  'sarcini',
  'nustiu',
] as const;
export type SituatieJuridica = (typeof SITUATIE_JURIDICA_VALUES)[number];

export const FILE_KINDS = ['photo', 'plan'] as const;
export type FileKind = (typeof FILE_KINDS)[number];

/**
 * Accepted upload formats: images only. Enforced by extension/type here and by a
 * magic-byte content sniff on the server. There is deliberately no PDF — both
 * the interior photos and the apartment plan are images. HEIC is what an iPhone
 * shoots by default, so it is accepted and converted server-side.
 */
export const ACCEPTED_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
] as const;
export const ACCEPTED_IMAGE_ACCEPT_ATTR =
  '.jpg,.jpeg,.png,.webp,.heic,.heif,image/jpeg,image/png,image/webp,image/heic,image/heif';

/** Formats a browser can render inline in an <img>. HEIC/HEIF cannot, outside Safari. */
export const PREVIEWABLE_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

export const MAX_PHOTO_SIZE_BYTES = 10 * 1024 * 1024;
export const MAX_PHOTO_SIZE_MB = 10;
export const MAX_PHOTOS = 15;
/** Photos are recommended, not required. Below this the form nudges, never blocks. */
export const RECOMMENDED_MIN_PHOTOS = 3;

export const MAX_PLAN_SIZE_BYTES = 20 * 1024 * 1024;
export const MAX_PLAN_SIZE_MB = 20;

/** Step 1 output: where the apartment is and what the building is. */
export interface ApartmentInfo {
  /** The formatted address string — from a picked suggestion or typed by hand. */
  address: string;
  /** Coordinates of the picked suggestion; null when the address was typed freehand. */
  lat: number | null;
  lng: number | null;
  anConstructie: AnConstructie | '';
  tipImobil: TipImobil | '';
  /** Held as strings so "parter"/"demisol" and partial input stay editable. */
  etaj: string;
  etajeTotal: string;
  /** m², held as a string so partial input stays editable (see parseDecimal). */
  suprafataUtila: string;
  numarCamere: NumarCamere | '';
  incadrareSeismica: IncadrareSeismica | '';
  // --- Apartment-only (shown when tipImobil is apartament or unset) ---
  /** Whether the building has a lift (checkbox); drives the no-lift price penalty. */
  lift: boolean;
  compartimentare: Compartimentare | '';
  /** Balcony type. When open/closed, the owner may also give its area below. */
  balcon: Balcon | '';
  /** Balcony area m², shown only for an open/closed balcony (string, see parseDecimal). */
  suprafataBalcon: string;
  sursaIncalzire: SursaIncalzire | '';
  // --- House-only (shown when tipImobil is casa or altul) ---
  /** Plot area, m², held as a string like suprafataUtila (see parseDecimal). */
  suprafataTeren: string;
  structura: Structura | '';
  incalzire: Incalzire | '';
}

/** Step 2 output, minus the files. */
export interface ConditionInfo {
  stareActuala: Stare | '';
  situatieJuridica: SituatieJuridica | '';
  intentieNote: string;
}

/** Step 3 output. */
export interface OwnerContact {
  name: string;
  email: string;
  phone: string;
  consent: boolean;
}

export interface SelectedFile {
  /** Stable key for React lists and removal — File objects are not identity-safe. */
  id: string;
  file: File;
  /**
   * Object URL for an inline thumbnail, or null for a format the browser cannot
   * render (HEIC/HEIF outside Safari) — those show a placeholder tile. Created
   * once at add time and revoked on removal, so it must live on the object.
   */
  previewUrl: string | null;
}

export interface SubmissionFiles {
  /** Interior photos — the centerpiece. Max MAX_PHOTOS. */
  photos: SelectedFile[];
  /** The apartment plan — a single image. */
  plan: SelectedFile | null;
}

/** Everything the wizard collects, held in one place at the App level. */
export interface WizardState {
  apartment: ApartmentInfo;
  condition: ConditionInfo;
  contact: OwnerContact;
  files: SubmissionFiles;
}

/** The JSON `payload` part of the multipart POST. Mirrored by the server schema. */
export interface SubmissionPayload {
  locale: string;
  address: string;
  lat: number | null;
  lng: number | null;
  anConstructie: AnConstructie | null;
  tipImobil: TipImobil | null;
  etaj: string | null;
  etajeTotal: string | null;
  suprafataUtila: number | null;
  numarCamere: NumarCamere | null;
  incadrareSeismica: IncadrareSeismica | null;
  lift: boolean;
  compartimentare: Compartimentare | null;
  balcon: Balcon | null;
  suprafataBalcon: number | null;
  sursaIncalzire: SursaIncalzire | null;
  suprafataTeren: number | null;
  structura: Structura | null;
  incalzire: Incalzire | null;
  stareActuala: Stare | null;
  situatieJuridica: SituatieJuridica | null;
  intentieNote: string;
  ownerName: string;
  ownerEmail: string;
  ownerPhone: string;
  consentGiven: boolean;
  consentTimestamp: string;
  referrer: string | null;
}

export type SubmissionSuccess = { ok: true; id: string };

export type SubmissionErrorCode =
  | 'validation'
  | 'rate_limit'
  | 'too_large'
  | 'network'
  | 'server';

export interface SubmissionFailure {
  ok: false;
  code: SubmissionErrorCode;
  /** Field-keyed messages from the server, when it rejects specific fields. */
  fields?: Record<string, string>;
}

export type SubmissionResult = SubmissionSuccess | SubmissionFailure;
